// Infra (APS-336): Northflank's month so far and run-rate estimate, each project by kind, the
// services, addons and volumes (APS-528); ClickHouse Cloud's month by kind and its services
// (APS-534); and Cloudflare's Workers, queues, R2 and KV, without opening any of them; the cost per
// day and the month's budget. Each source is read at most once a
// minute and says on its own when it is not set up or down.
// Console D (APS-412, admin-d.md E): the warnings as status lines, a strip of four, tables in
// `.t`, the days as a line chart with each line's name at its end, the budget in a form window.
import {
  ErrorState as ErrorLine,
  KV,
  LineChart,
  PageHead,
  Section,
  SectionHead,
  Status,
  Strip,
} from '@apsio/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useId, useState } from 'react'
import { type Infra, infraQuery, setInfraBudget } from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DataTable, NotConfigured } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { axisDay, duration, percent } from '../lib/format'

type SourceState = {
  status: 'ok' | 'not_configured' | 'unavailable'
  at: string | null
  reason: 'refused' | 'failed' | null
}

type Warning = Infra['warnings'][number]

const WARNING: Record<Warning, MessageId> = {
  queue_backlog: 'infra.warning.queue_backlog',
  dlq_messages: 'infra.warning.dlq_messages',
  worker_errors: 'infra.warning.worker_errors',
  over_budget: 'infra.warning.over_budget',
  budget_unknown: 'infra.warning.budget_unknown',
}

const SOURCE: Record<Infra['budget_blocked_by'][number]['source'], MessageId> = {
  northflank: 'infra.northflank',
  clickhouse: 'infra.clickhouse',
  cloudflare_r2: 'infra.blocker.cloudflare_r2',
  cloudflare_kv: 'infra.blocker.cloudflare_kv',
}

/** The warnings in crash colour: something is down or about to be; the rest are warn. */
const CRASH: ReadonlySet<Warning> = new Set(['dlq_messages', 'worker_errors'])

function Source({ section, children }: { section: SourceState; children: ReactNode }) {
  const { t } = useI18n()
  if (section.status === 'not_configured')
    return <NotConfigured missing={t('infra.notConfigured')} task={t('kit.ownerTask')} />
  // A refused key (401, 403) does not come back by itself: say so, and what to do.
  if (section.status === 'unavailable')
    return (
      <ErrorLine
        message={t(section.reason === 'refused' ? 'infra.refused' : 'infra.unavailable')}
      />
    )
  return <>{children}</>
}

/** Northflank's deploy statuses in the admin's words; any other reads as Northflank sends it. */
const DEPLOY: Record<string, MessageId> = {
  COMPLETED: 'infra.deploy.completed',
  FAILED: 'infra.deploy.failed',
  IN_PROGRESS: 'infra.deploy.in_progress',
  PENDING: 'infra.deploy.pending',
}

/** A deploy's status as Console D's glyph and word: ✓ ok, × falhou in crash colour, ○ the rest. */
function Deploy({ status }: { status: string }) {
  const { t } = useI18n()
  const word = DEPLOY[status] ? t(DEPLOY[status] as MessageId) : status.toLowerCase()
  if (status === 'COMPLETED') return <Status s="ok" glyph="✓" label={word} />
  if (status === 'FAILED') return <Status s="fire" glyph="×" label={word} />
  return <Status s="on" glyph="○" label={word} />
}

/** Northflank's addon states as the admin's word and Console D's glyph; any other reads as sent. */
const ADDON: Record<string, { word: MessageId; s: 'ok' | 'fire' | 'miss' | 'on'; glyph: string }> =
  {
    running: { word: 'infra.addon.running', s: 'ok', glyph: '✓' },
    paused: { word: 'infra.addon.paused', s: 'miss', glyph: '!' },
    failed: { word: 'infra.addon.failed', s: 'fire', glyph: '×' },
    error: { word: 'infra.addon.failed', s: 'fire', glyph: '×' },
    errorAllocating: { word: 'infra.addon.failed', s: 'fire', glyph: '×' },
    preDeployment: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
    triggerAllocation: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
    allocating: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
    postDeployment: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
    scaling: { word: 'infra.addon.scaling', s: 'on', glyph: '○' },
    upgrading: { word: 'infra.addon.upgrading', s: 'on', glyph: '○' },
    backup: { word: 'infra.addon.backingUp', s: 'on', glyph: '○' },
    // A reset or a restore replaces the data: warn.
    resetting: { word: 'infra.addon.resetting', s: 'miss', glyph: '!' },
    restore: { word: 'infra.addon.restoring', s: 'miss', glyph: '!' },
    deleting: { word: 'infra.addon.removing', s: 'miss', glyph: '!' },
    deleted: { word: 'infra.addon.removed', s: 'miss', glyph: '!' },
  }

function AddonStatus({ status }: { status: string }) {
  const { t } = useI18n()
  const known = ADDON[status]
  if (!known) return <Status s="on" glyph="○" label={status.toLowerCase()} />
  return <Status s={known.s} glyph={known.glyph} label={t(known.word)} />
}

/** ClickHouse Cloud's service states as the admin's word and Console D's glyph; others as sent. */
const SERVICE: Record<
  string,
  { word: MessageId; s: 'ok' | 'fire' | 'miss' | 'on'; glyph: string }
> = {
  running: { word: 'infra.addon.running', s: 'ok', glyph: '✓' },
  // Scaled to zero while nothing queries it: normal with idle scaling on.
  idle: { word: 'infra.service.idle', s: 'on', glyph: '○' },
  starting: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
  awaking: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
  provisioning: { word: 'infra.addon.starting', s: 'on', glyph: '○' },
  stopping: { word: 'infra.service.stopping', s: 'miss', glyph: '!' },
  stopped: { word: 'infra.service.stopped', s: 'miss', glyph: '!' },
  partially_running: { word: 'infra.service.degraded', s: 'miss', glyph: '!' },
  degraded: { word: 'infra.service.degraded', s: 'miss', glyph: '!' },
  failed: { word: 'infra.addon.failed', s: 'fire', glyph: '×' },
  terminating: { word: 'infra.addon.removing', s: 'miss', glyph: '!' },
  softdeleting: { word: 'infra.addon.removing', s: 'miss', glyph: '!' },
  terminated: { word: 'infra.addon.removed', s: 'miss', glyph: '!' },
  softdeleted: { word: 'infra.addon.removed', s: 'miss', glyph: '!' },
}

function ServiceState({ state }: { state: string }) {
  const { t } = useI18n()
  const known = SERVICE[state]
  if (!known) return <Status s="on" glyph="○" label={state.toLowerCase()} />
  return <Status s={known.s} glyph={known.glyph} label={t(known.word)} />
}

/**
 * The month's budget (APS-336): the figure, and Editar opens the form window (the kit's
 * ActionForm) with the amount and the reason (audited). An empty amount removes the budget.
 */
function Budget({ current }: { current: number | null }) {
  const { t, number } = useI18n()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [saved, setSaved] = useState(false)
  const id = useId()
  const save = useMutation({
    mutationFn: setInfraBudget,
    onSuccess: () => client.invalidateQueries({ queryKey: ['infra'] }),
  })
  return (
    <Section
      title={t('infra.budget.title')}
      meta={t('infra.budget.meta')}
      right={
        open ? undefined : (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              setSaved(false)
              setOpen(true)
            }}
          >
            {t('infra.budget.edit')}
          </button>
        )
      }
    >
      <KV
        items={[
          [
            t('infra.budget.label'),
            current === null
              ? t('infra.budget.none')
              : number(current, { style: 'currency', currency: 'USD' }),
          ],
        ]}
      />
      {saved && !open ? (
        <p role="status" className="nc-line">
          {t('infra.budget.saved')}
        </p>
      ) : null}
      {open ? (
        <div className="ops-win">
          <ActionForm
            open
            onClose={() => setOpen(false)}
            label={t('infra.budget.title')}
            confirm={t('infra.budget.save')}
            onSubmit={async (reason, form) => {
              const value = String(form.get('budget') ?? '').trim()
              await save.mutateAsync({ budgetUsd: value === '' ? null : Number(value), reason })
              setSaved(true)
            }}
          >
            <div className="field-d">
              <label className="lbl" htmlFor={id}>
                {t('infra.budget.label')}
              </label>
              <input
                id={id}
                name="budget"
                className="input mono"
                inputMode="decimal"
                autoComplete="off"
                defaultValue={current === null ? '' : String(current)}
                placeholder={t('infra.budget.none')}
              />
            </div>
          </ActionForm>
        </div>
      ) : null}
    </Section>
  )
}

export function InfraPage() {
  const { t, number, date, dateTime } = useI18n()
  const infra = useQuery(infraQuery)
  const usd = (n: number | null) =>
    n === null ? '—' : number(n, { style: 'currency', currency: 'USD' })
  const count = (n: number) => number(n)
  const gb = (bytes: number) =>
    number(bytes / 1e9, { style: 'unit', unit: 'gigabyte', maximumFractionDigits: 2 })
  const pct = (part: number, whole: number) => (whole > 0 ? percent(number, part / whole) : '—')
  const when = (iso: string | null) => (iso ? dateTime(iso) : '—')
  // A calendar day (YYYY-MM-DD) in the locale's short form, '7 de out.', as the other dates read.
  const day = (d: string) =>
    date(`${d}T12:00:00Z`, { day: 'numeric', month: 'short', timeZone: 'UTC' })

  if (infra.isPending || infra.isError)
    return (
      <main id="main" className="pg ops">
        <PageHead title={t('infra.title')} />
        {infra.isPending ? (
          <Loading />
        ) : (
          <ErrorState error={infra.error} retry={() => infra.refetch()} />
        )}
      </main>
    )
  const v = infra.data
  const billing = v.northflank.billing.data
  const resources = v.northflank.resources.data
  const ch = v.clickhouse.billing.data
  const chServices = v.clickhouse.services.data
  const chRefused = [v.clickhouse.billing, v.clickhouse.services].some(
    (x) => x.status === 'unavailable' && x.reason === 'refused',
  )
  // When the API last read a source: the latest of their reads (each is cached a minute).
  const reads = [
    v.northflank.billing,
    v.northflank.resources,
    v.clickhouse.billing,
    v.clickhouse.services,
    ...Object.values(v.cloudflare),
  ].flatMap((x) => (x.at ? [Date.parse(x.at)] : []))
  const readAt = reads.length > 0 ? Math.max(...reads) : infra.dataUpdatedAt
  const memory = (min: number | null, max: number | null) =>
    min === null || max === null
      ? '—'
      : min === max
        ? number(min, { style: 'unit', unit: 'gigabyte' })
        : `${number(min)}–${number(max, { style: 'unit', unit: 'gigabyte' })}`
  // Northflank sizes storage in MiB (10240 for 10 GB): whole gigabytes, as R2's read.
  const mb = (n: number | null) =>
    n === null
      ? '—'
      : number(n / 1024, { style: 'unit', unit: 'gigabyte', maximumFractionDigits: 1 })

  return (
    <main id="main" className="pg ops">
      <PageHead
        title={t('infra.title')}
        meta={`${
          v.warnings.length === 0
            ? t('infra.allClear')
            : t('infra.warnings', { n: v.warnings.length })
        } · ${t('infra.read', { time: date(readAt, { hour: '2-digit', minute: '2-digit' }) })}`}
      />

      {v.warnings.length > 0 ? (
        <Section title={t('infra.warningsTitle')}>
          <ul className="ops-warn">
            {[...v.warnings]
              // Crash first, then warn, each in the API's order.
              .sort((a, b) => Number(CRASH.has(b)) - Number(CRASH.has(a)))
              .map((w) => (
                <li key={w}>
                  {CRASH.has(w) ? (
                    <Status s="fire" glyph="×" label={t('infra.level.crash')} />
                  ) : (
                    <Status s="miss" glyph="!" label={t('infra.level.warn')} />
                  )}
                  <span>
                    {w === 'budget_unknown'
                      ? t(WARNING[w], {
                          sources: v.budget_blocked_by
                            .map((b) =>
                              t(`infra.blocker.${b.reason}`, { source: t(SOURCE[b.source]) }),
                            )
                            .join('; '),
                        })
                      : t(WARNING[w])}
                  </span>
                </li>
              ))}
          </ul>
        </Section>
      ) : null}

      <Section
        title={t('infra.northflank')}
        meta={
          billing
            ? t('infra.month', {
                month: date(billing.period_start, {
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              })
            : undefined
        }
      >
        <Source section={v.northflank.billing}>
          {billing ? (
            <>
              <Strip
                items={[
                  {
                    dt: t('infra.usage'),
                    dd: usd(billing.current_usage_usd),
                    d: t('infra.usageNote'),
                  },
                  {
                    dt: t('infra.estimate'),
                    dd: usd(billing.estimated_usd),
                    d: t('infra.estimateNote'),
                  },
                  {
                    dt: t('infra.credit'),
                    dd: usd(billing.credit_usd),
                    d: billing.credit_usd === null ? undefined : t('infra.creditNote'),
                  },
                  {
                    dt: t('infra.balance'),
                    dd: usd(billing.balance_usd),
                    // Signed: owed when negative; a positive one is already in the credit.
                    d:
                      billing.balance_usd === null || billing.balance_usd === 0
                        ? undefined
                        : billing.balance_usd < 0
                          ? t('infra.balanceOwed')
                          : t('infra.balanceInCredit'),
                  },
                ]}
              />
              {/* For reference only: the budget and the estimate are Apsio's projects (Abacus). */}
              <KV items={[[t('infra.team'), usd(billing.team_usage_usd)]]} />
              {billing.projects.length > 0 ? (
                <>
                  <SectionHead minor title={t('infra.projects')} meta={t('infra.projectsMeta')} />
                  <DataTable label={t('infra.projects')} minWidth={620}>
                    <thead>
                      <tr>
                        <th className="f" scope="col">
                          {t('infra.col.project')}
                        </th>
                        <th className="num" scope="col">
                          {t('infra.col.services')}
                        </th>
                        <th className="num" scope="col">
                          {t('infra.col.jobs')}
                        </th>
                        <th className="num" scope="col">
                          {t('infra.col.addons')}
                        </th>
                        <th className="num" scope="col">
                          {t('infra.col.volumes')}
                        </th>
                        <th className="num last" scope="col">
                          {t('infra.col.month')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.projects.map((p) => (
                        <tr key={p.project_id}>
                          <td className="f" translate="no">
                            {p.name}
                          </td>
                          <td className="num">{usd(p.by_kind.service)}</td>
                          <td className="num">{usd(p.by_kind.job)}</td>
                          <td className="num">{usd(p.by_kind.addon)}</td>
                          <td className="num">{usd(p.by_kind.volume)}</td>
                          <td className="num last">{usd(p.current_usage_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </>
              ) : null}
            </>
          ) : null}
        </Source>
        <SectionHead minor title={t('infra.services')} meta={t('infra.monthMeta')} />
        <Source section={v.northflank.resources}>
          {resources ? (
            <>
              <DataTable label={t('infra.services')} minWidth={820}>
                <thead>
                  <tr>
                    <th className="f" scope="col">
                      {t('infra.col.service')}
                    </th>
                    <th scope="col">{t('infra.col.project')}</th>
                    <th scope="col">{t('infra.col.deploy')}</th>
                    <th scope="col">{t('infra.col.commit')}</th>
                    <th scope="col">{t('infra.col.plan')}</th>
                    <th className="num" scope="col">
                      {t('infra.col.instances')}
                    </th>
                    <th className="num last" scope="col">
                      {t('infra.col.month')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resources.services.map((x) => (
                    <tr key={`${x.project_id}/${x.service_id}`}>
                      <td className="f" translate="no">
                        {x.name}
                      </td>
                      <td translate="no">{x.project}</td>
                      <td>
                        {x.deploy ? (
                          <>
                            <Deploy status={x.deploy.status} />{' '}
                            <span className="dim">{when(x.deploy.at)}</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td translate="no">{x.deploy?.commit ? x.deploy.commit.slice(0, 8) : '—'}</td>
                      <td translate="no">{x.plan ?? '—'}</td>
                      <td className="num">{x.instances ?? '—'}</td>
                      <td className="num last">{usd(x.month_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              {resources.addons.length > 0 ? (
                <>
                  <SectionHead minor title={t('infra.addons')} />
                  <DataTable label={t('infra.addons')} minWidth={720}>
                    <thead>
                      <tr>
                        <th className="f" scope="col">
                          {t('infra.col.addon')}
                        </th>
                        <th scope="col">{t('infra.col.project')}</th>
                        <th scope="col">{t('infra.col.type')}</th>
                        <th scope="col">{t('infra.col.status')}</th>
                        <th scope="col">{t('infra.col.tls')}</th>
                        <th className="num" scope="col">
                          {t('infra.col.size')}
                        </th>
                        <th className="num last" scope="col">
                          {t('infra.col.month')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.addons.map((x) => (
                        <tr key={`${x.project_id}/${x.addon_id}`}>
                          <td className="f" translate="no">
                            {x.name}
                          </td>
                          <td translate="no">{x.project}</td>
                          <td translate="no">
                            {x.type}
                            {x.version ? <span className="dim"> {x.version}</span> : null}
                          </td>
                          <td>
                            <AddonStatus status={x.status} />
                          </td>
                          <td>{x.tls ? t('infra.tls.on') : t('infra.tls.off')}</td>
                          <td className="num">{mb(x.storage_mb)}</td>
                          <td className="num last">{usd(x.month_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </>
              ) : null}
              {resources.volumes.length > 0 ? (
                <>
                  <SectionHead minor title={t('infra.volumes')} />
                  <DataTable label={t('infra.volumes')} minWidth={640}>
                    <thead>
                      <tr>
                        <th className="f" scope="col">
                          {t('infra.col.volume')}
                        </th>
                        <th scope="col">{t('infra.col.project')}</th>
                        <th scope="col">{t('infra.col.attached')}</th>
                        <th scope="col">{t('infra.col.status')}</th>
                        <th className="num" scope="col">
                          {t('infra.col.size')}
                        </th>
                        <th className="num last" scope="col">
                          {t('infra.col.month')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.volumes.map((x) => (
                        <tr key={`${x.project_id}/${x.volume_id}`}>
                          <td className="f" translate="no">
                            {x.name}
                          </td>
                          <td translate="no">{x.project}</td>
                          <td translate="no">
                            {x.attached_to.length > 0 ? x.attached_to.join(', ') : '—'}
                          </td>
                          <td>
                            {/* Worded once a volume exists; raw until then, as the others read. */}
                            <Status s="on" glyph="○" label={x.status.toLowerCase()} />
                          </td>
                          <td className="num">{mb(x.size_mb)}</td>
                          <td className="num last">{usd(x.month_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </>
              ) : null}
            </>
          ) : null}
        </Source>
      </Section>

      <Section
        title={t('infra.clickhouse')}
        meta={
          ch
            ? t('infra.month', {
                month: date(ch.period_start, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
              })
            : undefined
        }
      >
        {/* A refused key says so once, under the section's head: the Services below would only
            say it again. */}
        {chRefused ? (
          <ErrorLine message={t('infra.refused')} />
        ) : (
          <>
            <Source section={v.clickhouse.billing}>
              {ch ? (
                <>
                  <Strip
                    items={[
                      {
                        dt: t('infra.usage'),
                        dd: usd(ch.current_usage_usd),
                        d: t('infra.usageNote'),
                      },
                      {
                        dt: t('infra.estimate'),
                        dd: usd(ch.estimated_usd),
                        d: t('infra.estimateNote'),
                      },
                      {
                        dt: t('infra.credit'),
                        dd: usd(ch.credit_usd),
                        d: ch.credit_usd === null ? undefined : t('infra.creditNote'),
                      },
                    ]}
                  />
                  <KV
                    items={[
                      [t('infra.kind.storage'), usd(ch.by_kind.storage)],
                      [t('infra.kind.backup'), usd(ch.by_kind.backup)],
                      [t('infra.kind.compute'), usd(ch.by_kind.compute)],
                      [t('infra.kind.transfer'), usd(ch.by_kind.transfer)],
                      // For reference only: the budget and the estimate are Apsio's warehouse.
                      [t('infra.clickhouseOrganization'), usd(ch.organization_usage_usd)],
                    ]}
                  />
                </>
              ) : null}
            </Source>
            <SectionHead
              minor
              title={t('infra.services')}
              meta={t('infra.clickhouseServicesMeta')}
            />
            <Source section={v.clickhouse.services}>
              {chServices ? (
                <DataTable label={t('infra.clickhouseServices')} minWidth={680}>
                  <thead>
                    <tr>
                      <th className="f" scope="col">
                        {t('infra.col.service')}
                      </th>
                      <th scope="col">{t('infra.col.status')}</th>
                      <th scope="col">{t('infra.col.region')}</th>
                      <th className="num" scope="col">
                        {t('infra.col.replicas')}
                      </th>
                      <th className="num" scope="col">
                        {t('infra.col.memory')}
                      </th>
                      <th scope="col">{t('infra.col.idleScaling')}</th>
                      <th className="num last" scope="col">
                        {t('infra.col.month')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {chServices.map((x) => (
                      <tr key={x.service_id}>
                        <td className="f" translate="no">
                          {x.name}
                        </td>
                        <td>
                          <ServiceState state={x.state} />
                        </td>
                        <td translate="no">
                          {[x.provider, x.region].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="num">{x.replicas ?? '—'}</td>
                        <td className="num">
                          {memory(x.min_replica_memory_gb, x.max_replica_memory_gb)}
                        </td>
                        <td>
                          {x.idle_scaling === null
                            ? '—'
                            : x.idle_scaling
                              ? t('infra.tls.on')
                              : t('infra.tls.off')}
                        </td>
                        <td className="num last">{usd(x.month_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              ) : null}
            </Source>
          </>
        )}
      </Section>

      <Section title={t('infra.workers')} meta={t('infra.workersMeta')}>
        <Source section={v.cloudflare.workers}>
          <DataTable label={t('table.of', { name: t('infra.workers') })} minWidth={680}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('infra.col.worker')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.requestsHour')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.errorsHour')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.cpu')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.requestsDay')}
                </th>
                <th className="last" scope="col">
                  {t('infra.col.deploy')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(v.cloudflare.workers.data ?? []).map((w) => (
                <tr key={w.script}>
                  <td className="f" translate="no">
                    {w.script}
                  </td>
                  <td className="num">{count(w.last_hour.requests)}</td>
                  <td className="num">{pct(w.last_hour.errors, w.last_hour.requests)}</td>
                  <td className="num">
                    {w.last_hour.cpu_p50_ms === null
                      ? '—'
                      : `${number(w.last_hour.cpu_p50_ms)} / ${number(w.last_hour.cpu_p99_ms ?? 0)} ms`}
                  </td>
                  <td className="num">{count(w.last_day.requests)}</td>
                  <td className="last">{w.deployment ? when(w.deployment.at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Source>
      </Section>

      <Section title={t('infra.queues')}>
        <Source section={v.cloudflare.queues}>
          <DataTable label={t('table.of', { name: t('infra.queues') })} minWidth={480}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('infra.col.queue')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.waiting')}
                </th>
                <th className="num last" scope="col">
                  {t('infra.col.oldest')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(v.cloudflare.queues.data ?? []).map((q) => (
                <tr key={q.name}>
                  <td className="f" translate="no">
                    {q.name}
                    {q.dlq ? <span className="dim"> · {t('infra.dlq')}</span> : null}
                  </td>
                  <td className="num">{count(q.messages)}</td>
                  <td className="num last">
                    {q.oldest_age_seconds === null
                      ? '—'
                      : duration(number, q.oldest_age_seconds * 1000)}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Source>
      </Section>

      <Section title={t('infra.storage')} meta={t('infra.storageMeta')}>
        <Source section={v.cloudflare.r2}>
          <DataTable label={t('table.of', { name: t('infra.storage') })} minWidth={600}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('infra.col.bucket')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.objects')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.stored')}
                </th>
                <th className="num" scope="col">
                  {t('infra.col.classA')}
                </th>
                <th className="num last" scope="col">
                  {t('infra.col.classB')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(v.cloudflare.r2.data ?? []).map((b) => (
                <tr key={b.bucket}>
                  <td className="f" translate="no">
                    {b.bucket}
                  </td>
                  <td className="num">{count(b.objects)}</td>
                  <td className="num">{gb(b.bytes)}</td>
                  <td className="num">{count(b.class_a)}</td>
                  <td className="num last">{count(b.class_b)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Source>
        <SectionHead minor title={t('infra.kv')} meta={t('infra.kvMeta')} />
        <Source section={v.cloudflare.kv}>
          {v.cloudflare.kv.data ? (
            <Strip
              items={[
                { dt: t('infra.kv.reads'), dd: count(v.cloudflare.kv.data.reads) },
                { dt: t('infra.kv.writes'), dd: count(v.cloudflare.kv.data.writes) },
                { dt: t('infra.kv.deletes'), dd: count(v.cloudflare.kv.data.deletes) },
                { dt: t('infra.kv.lists'), dd: count(v.cloudflare.kv.data.lists) },
              ]}
            />
          ) : null}
        </Source>
      </Section>

      <Section
        title={t('infra.trend')}
        meta={
          v.month_estimate_usd === null
            ? undefined
            : t('infra.monthEstimate', {
                usd: usd(v.month_estimate_usd),
                budget: usd(v.budget_usd),
              })
        }
      >
        {v.trend.length === 0 ? (
          <Empty title={t('infra.trendEmptyTitle')}>{t('infra.trendEmpty')}</Empty>
        ) : (
          <div className="ops-chart">
            <LineChart
              label={t('infra.trendChart', {
                from: day(v.trend[0]?.day ?? ''),
                to: day(v.trend.at(-1)?.day ?? ''),
              })}
              name={t('infra.northflank')}
              points={v.trend.map((d) => ({
                label: axisDay(date, d.day),
                value: d.northflank,
              }))}
              lastLabel={(p) => `${t('infra.northflank')} ${usd(p.value)}`}
              more={[
                // ClickHouse Cloud's line once it has a day, so the panel without it draws none.
                ...(v.trend.some((d) => d.clickhouse > 0)
                  ? [
                      {
                        name: t('infra.clickhouse'),
                        values: v.trend.map((d) => d.clickhouse),
                        end: `${t('infra.clickhouse')} ${usd(v.trend.at(-1)?.clickhouse ?? null)}`,
                      },
                    ]
                  : []),
                {
                  name: t('infra.cloudflare'),
                  values: v.trend.map((d) => d.cloudflare),
                  end: `${t('infra.cloudflare')} ${usd(v.trend.at(-1)?.cloudflare ?? null)}`,
                },
              ]}
              legend
              boxLegend
              fromZero
              endLabelsFrom={640}
              format={(n) => number(n, { style: 'currency', currency: 'USD' })}
            />
          </div>
        )}
      </Section>

      <Budget current={v.budget_usd} />
    </main>
  )
}
